import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, ConfigurationSourceData, Trial } from "@/api/dtos";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import TrialContext from "@/contexts/data/TrialContext";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, ListItemIcon, ListItemText, Tooltip, styled } from "@mui/material";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListFiltersComponentProps, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { StatusListItemButtonTooltip, StatusListItemIconBox, StatusListItemLoadingButton, StatusListStatsBox } from './StatusListStats';
import { Link } from 'react-router-dom';
import ConfigurationSourceDataDeleteDialog from "@/components/dialogs/ConfigurationSourceDataDeleteDialog";


const AddButtonBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    //marginBottom: theme.spacing(3),
    minHeight: theme.spacing(4.75)
}));

const AddButton = styled(Button)(({ theme }) => ({
    minWidth: 0,
    padding: theme.spacing(0),
    width: theme.spacing(4.75),
    alignSelf: 'stretch'
}));

export interface IConfigurationSourceDataConfigurationListAdditional {
    trial?: Trial;
    deleteConfiguration: (configurationSourceDataId?: number) => Promise<boolean>;
}

export interface IConfigurationSourceDataStatusListProps extends StatusListProps<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional> {
    deleteConfiguration: (configurationSourceDataId?: number) => Promise<boolean>;
}

const createConfigurationSourceDataStatusLabel: IStatusListCreateLabel<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional> = (itemData) => {
    return (
        <>
            <ListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.sourceData} />
            </ListItemIcon>
            <ListItemText 
                primary={itemData.data.name} 
                secondary={itemData.data.description}
            />
        </>
    )
};

const createConfigurationSourceDataLink: IStatusListCreateLink<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional> = (itemData) => {
    if (itemData.additional.trial) {
        return `/${itemData.additional.trial.oid}/configuration/source-data/${itemData.data.id}`
    }

    return `/configuration/source-data/${itemData.data.id}`;
};

const createConfigurationSourceDataConfigurationLink: IStatusListCreateConfigureLink<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional> = (itemData) => {
    return undefined;
};

const isConfigurationSourceDataDisabled: IStatusListIsDisabled<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional> = (itemData) => {
    return false;
};

const isConfigurationSourceDataListItemDisabled: IStatusListIsDisabled<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional> = (itemData) => {
    return false;
};

const CopyConfigurationSourceDataButton: FC<IStatusListStatsComponentProps<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional>> = ({
    itemData,
    theme,
}) => {
    
    const { data: trial } = useContext(TrialContext);

    return (
        <StatusListItemButtonTooltip
            placement="left"
            title={
                <>
                    <strong>Copy</strong>
                    <div>
                        Copy and create a new configuration
                    </div>
                </>
            }
            followCursor
        >
            <StatusListItemLoadingButton
                //@ts-ignore-next-line
                component={Link}
                to={`/${trial?.oid}/configuration/source-data/copy/${itemData.data.id}`}

            >
                <FontAwesomeIcon icon={AppSettings.icons.copy} size="xl" />
            </StatusListItemLoadingButton>
        </StatusListItemButtonTooltip>
    )
}

const DeleteConfigurationSourceDataButton: FC<IStatusListStatsComponentProps<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional>> = ({
    itemData,
    theme,

}) => {
    const [dialogOpen, setDialogOpen] = useState(false);

    const onClose = useCallback(() => {
        return setDialogOpen(false);
    }, [setDialogOpen]);

    return (
        <>
            <StatusListItemButtonTooltip
                placement="left"
                title={
                    <>
                        <strong>Delete</strong>
                        <div>
                            Delete the configuration
                        </div>
                    </>
                }
                followCursor
            >
                <StatusListItemLoadingButton
                    color="error"
                    onClick={() => setDialogOpen(true)}
                >
                    <FontAwesomeIcon icon={AppSettings.icons.delete} size="xl" />
                </StatusListItemLoadingButton>
            </StatusListItemButtonTooltip>
            <ConfigurationSourceDataDeleteDialog
                configurationSourceData={itemData.data}
                deleteConfiguration={itemData.additional.deleteConfiguration}
                open={dialogOpen}
                onClose={onClose}
            />
        </>
    )
}

const ConfigurationSourceDataStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional>) => {
    return (
        
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            <CopyConfigurationSourceDataButton itemData={itemData} theme={theme} />
            <DeleteConfigurationSourceDataButton itemData={itemData} theme={theme} />
        </StatusListStatsBox>
    )
};

const ConfigurationSourceDataFiltersComponent: FC<IStatusListFiltersComponentProps> = () => {
    
    const { data: trial } = useContext(TrialContext);

    return (
        <>
            <AddButtonBox sx={{ml:2}}>
                <Tooltip title="Trial Configuration">
                    <AddButton 
                        variant="contained" 
                        color="primary"
                        //@ts-ignore
                        component={Link}
                        to={`/${trial?.oid}/configuration/source-data/trial`}
                    >
                        <FontAwesomeIcon icon={AppSettings.icons.trial} size="lg" />
                    </AddButton>
                </Tooltip>
            </AddButtonBox>
            <AddButtonBox sx={{ml:2}}>
                <Tooltip title="Add">
                    <AddButton 
                        variant="contained" 
                        color="primary"
                        //@ts-ignore
                        component={Link}
                        to={`/${trial?.oid}/configuration/source-data/new`}
                    >
                        <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" />
                    </AddButton>
                </Tooltip>
            </AddButtonBox>
        </>
    )
};


const SortableFields: IStatusListSortable<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional>[] = [
    {
        name: 'Name',
        sort: (a, b) => a.data.name! > b.data.name! ? 1 : -1
    },
    DefaultSortableFields.status
]

const ConfigurationSourceDataConfigurationList: FC<IConfigurationSourceDataStatusListProps> = ({
    deleteConfiguration,
    ...statusListProps
}) => {
    const { data: trial } = useContext(TrialContext);

    const createConfigurationSourceDataAdditional: IStatusListCreateAdditional<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional> = useCallback((itemData) => {
        return {
            trial: trial,
            deleteConfiguration
        }
    }, [trial, deleteConfiguration]);

    

    const searchableFields: IStatusListSearch<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional, any, any>[] = [
            {
                key: 'name',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Name & Description',
                searchFn: (item, value) => value ?  
                    (item.data.name?.toLowerCase()?.includes(value?.toLowerCase()) ?? false) ||  
                    (item.data.description?.toLowerCase()?.includes(value?.toLowerCase()) ?? false) : 
                    false
            } as IStatusListSearch<ConfigurationSourceData, IConfigurationSourceDataConfigurationListAdditional, string>
        ]

        return output;
    }, []);

    return (
        <StatusList 
            {...statusListProps}
            createLabel={createConfigurationSourceDataStatusLabel}
            createLink={createConfigurationSourceDataLink}
            createConfigureLink={createConfigurationSourceDataConfigurationLink}
            isDisabled={isConfigurationSourceDataDisabled}
            isListItemDisabled={isConfigurationSourceDataListItemDisabled}
            createAdditional={createConfigurationSourceDataAdditional}
            StatsComponent={ConfigurationSourceDataStatsComponent}
            FiltersComponent={ConfigurationSourceDataFiltersComponent}
            sortKey="configuration-source-data-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Master Groups</AlertTitle>
                    No master groups were found on the {trial?.name} trial.
                </Alert>
            }
            searchFields={searchableFields}
            searchKey={`configuration-source-data-search-${trial?.oid}`}
        />
    )
};

export default ConfigurationSourceDataConfigurationList;